import React, { useEffect, useMemo, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { Box, Button, Typography } from '@material-ui/core';
import HttpService from '../services/httpService';
import { setSnackInfo } from '../redux/actions/appAction';
import { ROLENAME } from '../utils';

const CITY_HEADER_BG = '#1e3a8a';
const CITY_HEADER_FG = '#ffffff';
const MGR_ROW_BG = '#ffe8cc';
const MGR_ROW_FG = '#000000';
const EXEC_ROW_BG = '#fefce8';
const PM_CELL_BG = '#c2e7c1';
const PM_OPEN_REQ_BG = '#ccf2ff';
const TABLE_TEXT_COLOR = '#000000';
const CELL_BORDER_COLOR = '#000000';
const CELL_BORDER = `0.5px solid ${CELL_BORDER_COLOR}`;
const DEFAULT_CELL_BG = '#ffffff';
const PM_GRID_COLS = 3;

const CELL_TEXT_WRAP = {
  maxWidth: '100%',
  wordBreak: 'break-word',
  overflowWrap: 'anywhere',
};

function gridCellBorders(colIndex, rowIndex, colCount, rowCount) {
  return {
    boxSizing: 'border-box',
    borderColor: CELL_BORDER_COLOR,
    borderRight: colIndex < colCount - 1 ? CELL_BORDER : undefined,
    borderBottom: rowIndex < rowCount - 1 ? CELL_BORDER : undefined,
    minWidth: 0,
  };
}

function hexToRgba(hex, alpha = 0.32) {
  const h = String(hex || '').trim();
  if (!/^#[0-9a-fA-F]{6}$/.test(h)) return undefined;
  const n = parseInt(h.slice(1), 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function resolveRowStatus(row) {
  if (!row || typeof row !== 'object') return '';
  const nested = row.hrStatusColorCodeId || row.hrStatusColorCode;
  if (nested && typeof nested === 'object') {
    const fromCode = String(nested.status || '').trim();
    if (fromCode) return fromCode;
  }
  return String(row.status || row.rosterStatus || '').trim();
}

function resolveStatusKey(row) {
  if (!row) return '';
  const id =
    row.hrStatusColorCodeId?._id ||
    row.hrStatusColorCodeId ||
    row.hrStatusColorCode?._id ||
    row.hrStatusColorCode;
  if (id) return String(id);
  return resolveRowStatus(row);
}

function formatBracketStatusLabel(status) {
  const s = String(status || '').toLowerCase().replace(/\s+/g, ' ').trim();
  if (!s) return '';
  if (s === 'active') return 'Active';
  if (s === 'ok') return 'Ok';
  if (s.includes('open req')) return 'Open Req';
  if (s.includes('yet to join')) return 'Yet to Join';
  if (s.includes('background verification failed')) return 'Background Verification Failed';
  if (s.includes('buffer') && s.includes('idd')) return 'Buffer (IDD)';
  return String(status).trim();
}

function shouldShowStatusInBracket(status) {
  return Boolean(String(status || '').trim());
}

function isLastGroupMemberStatus(row) {
  if (!row || typeof row !== 'object') return false;
  const st = resolveRowStatus(row).toLowerCase().replace(/\s+/g, ' ').trim();
  const note = String(row.statusNote || '').toLowerCase().replace(/\s+/g, ' ').trim();
  const check = (s) =>
    s.includes('open req') ||
    s.includes('yet to join') ||
    (s.includes('buffer') && s.includes('idd')) ||
    s.includes('buffer(idd)');
  return check(st) || check(note);
}

function isOpenReqPerson(row) {
  if (!row || typeof row !== 'object') return false;
  return resolveRowStatus(row).toLowerCase().replace(/\s+/g, ' ').trim().includes('open req');
}

function sortMembersForDisplay(members) {
  const list = Array.isArray(members) ? members : [];
  const primary = [];
  const lastGroup = [];
  for (const member of list) {
    if (isLastGroupMemberStatus(member)) lastGroup.push(member);
    else primary.push(member);
  }
  return [...primary, ...lastGroup];
}

const MONTHS_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

function ordinalDay(n) {
  const j = n % 10;
  const k = n % 100;
  if (j === 1 && k !== 11) return `${n}st`;
  if (j === 2 && k !== 12) return `${n}nd`;
  if (j === 3 && k !== 13) return `${n}rd`;
  return `${n}th`;
}

function isInvalidJoiningYear(y) {
  return !y || y <= 1970 || y < 1980;
}

function formatJoiningFromYmd(y, m, day) {
  if (!m || !day || isInvalidJoiningYear(y)) return '';
  return `${ordinalDay(day)} ${MONTHS_SHORT[m - 1]} ${y}`;
}

function parseJoiningDateParts(value) {
  if (value == null || value === '') return null;
  const s = String(value).trim();
  if (!s) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, day] = s.split('-').map(Number);
    if (isInvalidJoiningYear(y)) return null;
    return { y, m, day };
  }
  const parsed =
    value instanceof Date
      ? value
      : typeof value === 'number' && Number.isFinite(value)
        ? new Date(value)
        : new Date(s);
  if (Number.isNaN(parsed.getTime())) return null;
  const y = parsed.getFullYear();
  if (isInvalidJoiningYear(y)) return null;
  return { y, m: parsed.getMonth() + 1, day: parsed.getDate() };
}

function formatJoining(value) {
  const parts = parseJoiningDateParts(value);
  if (!parts) return '';
  return formatJoiningFromYmd(parts.y, parts.m, parts.day);
}

function resolveStatusDateForRow(row) {
  if (!row || typeof row !== 'object') return null;
  const s = resolveRowStatus(row).toLowerCase().replace(/\s+/g, ' ').trim();
  if (s.includes('open req')) return row.openReqDate ?? null;
  if (s === 'relieved') return row.relievedDate ?? null;
  if (s === 'transferred') return row.transferredDate ?? null;
  if (s === 'terminated') return row.terminatedDate ?? null;
  if (s === 'maternity') return row.maternityDate ?? null;
  if (s === 'industry') return row.industryDate ?? null;
  if (s === 'abscond') return row.abscondDate ?? null;
  if (s === 'resigned') return row.resignedDate ?? null;
  if (s === 'warning') return row.warningDate ?? null;
  if ((s.includes('buffer') && s.includes('idd')) || s.includes('buffer(idd)')) {
    return row.bufferIddDate ?? null;
  }
  if (s === 'active - training' || (s.includes('active') && s.includes('training'))) {
    return row.activeTrainingDate ?? null;
  }
  if (s === 'pip') return row.pipDate ?? null;
  return null;
}

function resolveRawJoiningDate(row) {
  if (!row || typeof row !== 'object') return null;
  return (
    row.joiningDate ??
    row.joining_date ??
    row.dateOfJoining ??
    row.date_of_joining ??
    row.doj ??
    row.joinDate ??
    row.joinedOn ??
    row.user?.joiningDate ??
    row.employee?.joiningDate ??
    row.rosterUser?.joiningDate ??
    null
  );
}

function isJoiningDateOnlyStatus(normalized) {
  return (
    normalized === 'yet to join' ||
    normalized === 'active' ||
    (normalized.includes('active') && normalized.includes('training'))
  );
}

function getPersonDateLines(person) {
  if (!person || typeof person !== 'object') {
    return { statusDate: '', joiningDate: '' };
  }
  const normalized = resolveRowStatus(person).toLowerCase().replace(/\s+/g, ' ').trim();
  if (isJoiningDateOnlyStatus(normalized)) {
    return { statusDate: formatJoining(resolveRawJoiningDate(person)), joiningDate: '' };
  }
  return { statusDate: formatJoining(resolveStatusDateForRow(person)), joiningDate: '' };
}

function statusSuffixBracket(row) {
  if (!row || typeof row !== 'object') return '';
  const st = resolveRowStatus(row);
  const note = String(row.statusNote || '').trim();
  if (shouldShowStatusInBracket(st)) return ` (${formatBracketStatusLabel(st)})`;
  if (!st && shouldShowStatusInBracket(note)) return ` (${formatBracketStatusLabel(note)})`;
  return '';
}

function hasPersonName(person) {
  if (!person || typeof person !== 'object') return false;
  const normalized = resolveRowStatus(person).toLowerCase().replace(/\s+/g, ' ').trim();
  if (normalized.includes('open req')) return true;
  return Boolean(String(person?.name || person?.employeeName || '').trim());
}

function getPersonDisplayName(person) {
  if (!person || typeof person !== 'object') return '—';
  const name = String(person.name || person.employeeName || '').trim();
  if (name) return name;
  const normalized = resolveRowStatus(person).toLowerCase().replace(/\s+/g, ' ').trim();
  if (normalized.includes('open req')) return 'Open Req';
  return '—';
}

function isOpenReqStatusPerson(person) {
  if (!person || typeof person !== 'object') return false;
  return resolveRowStatus(person).toLowerCase().replace(/\s+/g, ' ').trim().includes('open req');
}

function getPersonNameLine(person) {
  const displayName = getPersonDisplayName(person);
  const hasRealName = Boolean(String(person?.name || person?.employeeName || '').trim());
  if (isOpenReqStatusPerson(person) && !hasRealName) {
    return displayName;
  }
  return `${displayName}${statusSuffixBracket(person)}`;
}

/** Use HR status color when configured; otherwise role default background. */
function statusBackgroundOr(row, getStatusCellBackground, fallbackBg) {
  if (!row || typeof row !== 'object') return fallbackBg;
  const bg = getStatusCellBackground(row);
  return bg && bg !== DEFAULT_CELL_BG ? bg : fallbackBg;
}

function somKey(som) {
  if (!som) return '__unassigned__';
  return String(som._id || som.emailId || som.name || '').trim() || '__unassigned__';
}

function PersonCellContent({ person, prefix, align = 'center', bold = false, compact = false }) {
  if (!person && !prefix) return null;
  if (!person) {
    return (
      <Typography
        component="span"
        style={{
          fontSize: compact ? 12 : 14,
          color: TABLE_TEXT_COLOR,
          fontWeight: bold || prefix ? 700 : 400,
          textAlign: align,
          display: 'block',
          ...CELL_TEXT_WRAP,
        }}
      >
        {prefix ? `${prefix}: —` : '—'}
      </Typography>
    );
  }

  const nameLine = getPersonNameLine(person);
  const { statusDate, joiningDate } = getPersonDateLines(person);

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      style={{ gap: 2, width: '100%', boxSizing: 'border-box' }}
    >
      <Typography
        component="span"
        style={{
          fontSize: compact ? 12 : 14,
          color: TABLE_TEXT_COLOR,
          fontWeight: bold || prefix ? 700 : 400,
          lineHeight: 1.35,
          textAlign: align,
          display: 'block',
          ...CELL_TEXT_WRAP,
        }}
      >
        {prefix ? `${prefix}: ${nameLine}` : nameLine}
      </Typography>
      {statusDate ? (
        <Typography
          component="span"
          style={{
            fontSize: 11,
            color: TABLE_TEXT_COLOR,
            lineHeight: 1.25,
            textAlign: align,
            display: 'block',
            ...CELL_TEXT_WRAP,
          }}
        >
          {statusDate}
        </Typography>
      ) : null}
      {joiningDate ? (
        <Typography
          component="span"
          style={{
            fontSize: 11,
            color: TABLE_TEXT_COLOR,
            lineHeight: 1.25,
            textAlign: align,
            display: 'block',
            ...CELL_TEXT_WRAP,
          }}
        >
          {joiningDate}
        </Typography>
      ) : null}
    </Box>
  );
}

/**
 * One Sr. Ops Mgr bucket with execution-user blocks (each block → PM grid).
 */
function buildSomSectionsForLocation(loc) {
  const seniorOperationManagers = loc.seniorOperationManagers || [];
  const executionUsers = (loc.executionLeads || loc.executionUsers || []).filter(hasPersonName);

  const bucketMap = new Map();

  const ensureBucket = (som) => {
    const k = somKey(som);
    if (!bucketMap.has(k)) {
      bucketMap.set(k, {
        som: som && hasPersonName(som) ? som : null,
        blocks: [],
      });
    }
    return bucketMap.get(k);
  };

  for (const s of seniorOperationManagers) {
    if (hasPersonName(s)) ensureBucket(s);
  }

  for (const ex of executionUsers) {
    const projectManagers = sortMembersForDisplay(
      Array.isArray(ex.projectManagers) ? ex.projectManagers.filter(hasPersonName) : []
    );
    if (projectManagers.length === 0) continue;

    const l1 = ex.l3SeniorOperationManager || ex.l1SeniorOperationManager;
    ensureBucket(hasPersonName(l1) ? l1 : null).blocks.push({
      exec: ex,
      projectManagers,
    });
  }

  const orderedKeys = [];
  for (const s of seniorOperationManagers) {
    if (hasPersonName(s)) {
      const k = somKey(s);
      if (!orderedKeys.includes(k)) orderedKeys.push(k);
    }
  }
  for (const k of bucketMap.keys()) {
    if (!orderedKeys.includes(k)) orderedKeys.push(k);
  }

  return orderedKeys
    .map((k) => ({ key: k, ...bucketMap.get(k) }))
    .filter((section) => (section.blocks || []).length > 0);
}

/** EC bucket key on person; empty string means "no EC" and is grouped as '—'. */
function personEcBucket(person) {
  const e = String(person?.ec || '').trim();
  return e || '—';
}

/**
 * New API shape: city has `dgmOps`, `seniorOperationManagers`, `executionUsers`
 * (no `locations`). Split into synthetic locations grouped by each person's `ec`.
 */
function normalizeFlatCityToLocations(cityBlock) {
  const city = cityBlock.city || '—';
  const dgmOps =
    cityBlock.dgmOps && hasPersonName(cityBlock.dgmOps) ? cityBlock.dgmOps : null;
  const soms = (cityBlock.seniorOperationManagers || []).filter(hasPersonName);
  const execs = (cityBlock.executionUsers || []).filter(hasPersonName);

  const ecSet = new Set();
  for (const s of soms) ecSet.add(personEcBucket(s));
  for (const e of execs) ecSet.add(personEcBucket(e));

  if (ecSet.size === 0) {
    if (!dgmOps && soms.length === 0 && execs.length === 0) return [];
    return [
      {
        ec: city,
        dgmOps,
        seniorOperationManagers: soms,
        executionUsers: execs,
      },
    ];
  }

  const ecList = Array.from(ecSet).sort((a, b) => {
    if (a === '—') return 1;
    if (b === '—') return -1;
    return a.localeCompare(b);
  });

  return ecList.map((ecBucket) => {
    const match = (p) => personEcBucket(p) === ecBucket;
    return {
      ec: ecBucket === '—' ? city : ecBucket,
      dgmOps,
      seniorOperationManagers: soms.filter(match),
      executionUsers: execs.filter(match),
    };
  });
}

function mapLocation(loc) {
  const base = {
    ec: loc.ec || loc.experienceCenter || loc.ecName || '—',
    city: loc.city || '',
    department: loc.department || '',
    dgmOps: loc.dgmOps && hasPersonName(loc.dgmOps) ? loc.dgmOps : null,
    seniorOperationManagers: (loc.seniorOperationManagers || []).filter(hasPersonName),
    executionUsers: (loc.executionLeads || loc.executionUsers || []).filter(hasPersonName),
  };
  return {
    ...base,
    somSections: buildSomSectionsForLocation(base),
  };
}

function buildOpsHierarchyView(apiData) {
  if (!apiData || typeof apiData !== 'object') return { cities: [] };
  if (!Array.isArray(apiData.cities)) return { cities: [] };

  return {
    cities: apiData.cities.map((cityBlock) => {
      const city = cityBlock.city || '—';
      const legacyLocs = Array.isArray(cityBlock.locations) ? cityBlock.locations : [];

      let locations;
      if (legacyLocs.length > 0) {
        locations = legacyLocs.map(mapLocation);
      } else {
        locations = normalizeFlatCityToLocations(cityBlock).map((loc) => ({
          ...mapLocation(loc),
        }));
      }

      locations = locations.filter((loc) => (loc.somSections || []).length > 0);

      locations.sort((a, b) =>
        String(a.ec).localeCompare(String(b.ec), undefined, { sensitivity: 'base' })
      );

      return { city, locations };
    }).filter((cityBlock) => (cityBlock.locations || []).length > 0),
  };
}

function HierarchyRow({ children, backgroundColor = MGR_ROW_BG }) {
  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      py={1.5}
      px={2}
      style={{
        backgroundColor,
        color: MGR_ROW_FG,
        border: CELL_BORDER,
        borderTop: 'none',
        borderColor: CELL_BORDER_COLOR,
        textAlign: 'center',
        width: '100%',
        boxSizing: 'border-box',
        flexShrink: 0,
        minHeight: 'fit-content',
        overflow: 'visible',
      }}
    >
      {children}
    </Box>
  );
}

function PmGridCell({ person, colIndex, rowIndex, colCount, rowCount, getStatusCellBackground }) {
  if (!person) {
    return (
      <Box
        py={1.5}
        px={0.5}
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        style={{
          ...gridCellBorders(colIndex, rowIndex, colCount, rowCount),
          backgroundColor: PM_OPEN_REQ_BG,
          minHeight: 76,
        }}
      >
        <Typography style={{ fontWeight: 700, fontSize: 14, color: TABLE_TEXT_COLOR, ...CELL_TEXT_WRAP }}>
          —
        </Typography>
        <Typography style={{ fontSize: 12, color: TABLE_TEXT_COLOR, ...CELL_TEXT_WRAP }}>
          (Open Req)
        </Typography>
      </Box>
    );
  }

  const nameLine = getPersonNameLine(person);
  const { statusDate, joiningDate } = getPersonDateLines(person);
  const openReq = isOpenReqPerson(person);
  const fallbackBg = openReq ? PM_OPEN_REQ_BG : PM_CELL_BG;
  const cellBg = statusBackgroundOr(person, getStatusCellBackground, fallbackBg);

  return (
    <Box
      py={1.5}
      px={0.5}
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      style={{
        ...gridCellBorders(colIndex, rowIndex, colCount, rowCount),
        backgroundColor: cellBg,
        minHeight: 76,
      }}
    >
      <Typography
        style={{
          fontWeight: 700,
          fontSize: 14,
          color: TABLE_TEXT_COLOR,
          textAlign: 'center',
          lineHeight: 1.35,
          ...CELL_TEXT_WRAP,
        }}
      >
        {nameLine}
      </Typography>
      {statusDate ? (
        <Typography style={{ fontSize: 11, color: TABLE_TEXT_COLOR, marginTop: 4, textAlign: 'center', ...CELL_TEXT_WRAP }}>
          {statusDate}
        </Typography>
      ) : null}
      {joiningDate ? (
        <Typography style={{ fontSize: 11, color: TABLE_TEXT_COLOR, marginTop: 4, textAlign: 'center', ...CELL_TEXT_WRAP }}>
          {joiningDate}
        </Typography>
      ) : null}
    </Box>
  );
}

function PmGrid({ projectManagers, parentKey, getStatusCellBackground }) {
  const pms = Array.isArray(projectManagers) ? projectManagers : [];

  if (pms.length === 0) {
    return (
      <Box
        py={2}
        px={2}
        style={{
          border: CELL_BORDER,
          borderTop: 'none',
          borderColor: CELL_BORDER_COLOR,
          textAlign: 'center',
          color: TABLE_TEXT_COLOR,
          backgroundColor: DEFAULT_CELL_BG,
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        No project managers
      </Box>
    );
  }

  const rowCount = Math.ceil(pms.length / PM_GRID_COLS);

  return (
    <Box
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${PM_GRID_COLS}, minmax(0, 1fr))`,
        border: CELL_BORDER,
        borderTop: 'none',
        borderColor: CELL_BORDER_COLOR,
        width: '100%',
        boxSizing: 'border-box',
        flexShrink: 0,
      }}
    >
      {pms.map((pm, i) => {
        const colIndex = i % PM_GRID_COLS;
        const rowIndex = Math.floor(i / PM_GRID_COLS);
        return (
          <PmGridCell
            key={pm._id || pm.emailId || `${parentKey}-pm-${i}`}
            person={pm}
            colIndex={colIndex}
            rowIndex={rowIndex}
            colCount={PM_GRID_COLS}
            rowCount={rowCount}
            getStatusCellBackground={getStatusCellBackground}
          />
        );
      })}
    </Box>
  );
}

function ExecUserRow({ person, getStatusCellBackground }) {
  const rowBg = statusBackgroundOr(person, getStatusCellBackground, EXEC_ROW_BG);
  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      py={1.5}
      px={2}
      style={{
        backgroundColor: rowBg,
        color: MGR_ROW_FG,
        border: CELL_BORDER,
        borderTop: 'none',
        borderColor: CELL_BORDER_COLOR,
        textAlign: 'center',
        width: '100%',
        boxSizing: 'border-box',
        flexShrink: 0,
        minHeight: 'fit-content',
        overflow: 'visible',
      }}
    >
      <PersonCellContent person={person} prefix="Execution Lead" bold />
    </Box>
  );
}

function SomSectionBlock({ section, locationKey, getStatusCellBackground }) {
  const { som, blocks } = section;
  const somRowBg = statusBackgroundOr(som, getStatusCellBackground, MGR_ROW_BG);

  return (
    <Box
      mb={0}
      display="flex"
      flexDirection="column"
      width="100%"
      style={{ boxSizing: 'border-box', flexShrink: 0 }}
    >
      <HierarchyRow backgroundColor={somRowBg}>
        <PersonCellContent person={som} prefix="Sr. Manager Operations" bold />
      </HierarchyRow>

      {blocks.length === 0 ? (
        <Box
          py={2}
          px={2}
          style={{
            border: CELL_BORDER,
            borderTop: 'none',
            borderColor: CELL_BORDER_COLOR,
            textAlign: 'center',
            color: TABLE_TEXT_COLOR,
            backgroundColor: DEFAULT_CELL_BG,
          }}
        >
          No execution leads
        </Box>
      ) : (
        blocks.map((block, bi) => (
          <Box
            key={block.exec?._id || `${locationKey}-ex-${bi}`}
            mb={0}
            display="flex"
            flexDirection="column"
            width="100%"
            style={{ boxSizing: 'border-box', flexShrink: 0 }}
          >
            <ExecUserRow person={block.exec} getStatusCellBackground={getStatusCellBackground} />
            <PmGrid
              projectManagers={block.projectManagers}
              parentKey={`${locationKey}-ex-${bi}`}
              getStatusCellBackground={getStatusCellBackground}
            />
          </Box>
        ))
      )}
    </Box>
  );
}

function LocationBlock({ loc, city, getStatusCellBackground }) {
  const locKey = `${city}-${loc.ec}`;
  const sections = loc.somSections || [];

  return (
    <Box
      display="flex"
      flexDirection="column"
      width="100%"
      style={{ boxSizing: 'border-box', flexShrink: 0 }}
    >
      <Box
        py={1}
        px={2}
        style={{
          backgroundColor: '#6b7280',
          color: '#ffffff',
          fontWeight: 700,
          textAlign: 'center',
          border: CELL_BORDER,
          borderTop: 'none',
          borderColor: CELL_BORDER_COLOR,
          width: '100%',
          boxSizing: 'border-box',
          flexShrink: 0,
        }}
      >
        {loc.ec}
      </Box>

      {sections.length === 0 ? (
        <Box
          py={2}
          px={2}
          style={{
            border: CELL_BORDER,
            borderTop: 'none',
            borderColor: CELL_BORDER_COLOR,
            textAlign: 'center',
            color: TABLE_TEXT_COLOR,
          }}
        >
          No senior operation managers or execution leads
        </Box>
      ) : (
        sections.map((section, si) => (
          <SomSectionBlock
            key={section.key || `sec-${locKey}-${si}`}
            section={section}
            locationKey={`${locKey}-${si}`}
            getStatusCellBackground={getStatusCellBackground}
          />
        ))
      )}
    </Box>
  );
}

function HrRacksheetOpsHierarchy(props) {
  const { userDetails } = useSelector((state) => state.user);
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hrStatusColorCodes, setHrStatusColorCodes] = useState([]);

  const loadHrStatusColorCodes = async () => {
    try {
      const response = await HttpService.getHrStatusColorCodes();
      setHrStatusColorCodes(Array.isArray(response?.data) ? response.data : []);
    } catch (error) {
      console.error('Error loading HR status color codes:', error);
      setHrStatusColorCodes([]);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const response = await HttpService.getRosterOperationsHierarchy();
      if (response?.data?.success === false) {
        props.setSnackInfo(
          response?.data?.message || 'Failed to load operations hierarchy',
          'error'
        );
        setPayload(null);
        return;
      }
      setPayload(response?.data?.data ?? response?.data ?? null);
    } catch (error) {
      console.error('Error loading operations hierarchy:', error);
      props.setSnackInfo(
        error?.response?.data?.message || 'Error loading operations hierarchy',
        'error'
      );
      setPayload(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    loadHrStatusColorCodes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getStatusCellBackground = useMemo(() => {
    return (row) => {
      const key = resolveStatusKey(row);
      if (!key) return DEFAULT_CELL_BG;
      const normalizedKey = key.toLowerCase();
      const hit = hrStatusColorCodes.find(
        (item) =>
          String(item?._id) === key ||
          String(item?.status || '').trim().toLowerCase() === normalizedKey
      );
      if (!hit?.hexCode) return DEFAULT_CELL_BG;
      return hexToRgba(hit.hexCode, 0.32) || hit.hexCode;
    };
  }, [hrStatusColorCodes]);

  const view = useMemo(() => {
    const built = buildOpsHierarchyView(payload);
    const hasFullAccess = userDetails?.roles?.some(
      (role) =>
        role?.name === ROLENAME.Admin ||
        role?.name === ROLENAME.HRUser ||
        role?.name === ROLENAME.HRManager || 
        role?.name === ROLENAME.HRAssistantManager
    );
    if (hasFullAccess) return built;
    const userCityNames = (userDetails?.locationId || [])
      .map((loc) => loc?.name)
      .filter(Boolean);
    return { cities: built.cities.filter((cityBlock) => userCityNames.includes(cityBlock.city)) };
  }, [payload, userDetails]);
  const title = (payload && payload.title) || 'Operations hierarchy — city & EC view';

  return (
    <Box p={2}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        style={{ gap: 12 }}
        mb={2}
      >
        <Typography variant="h5" style={{ fontWeight: 700, color: CITY_HEADER_BG }}>
          {title}
        </Typography>
        <Button
          variant="outlined"
          disabled={loading}
          onClick={() => {
            load();
            loadHrStatusColorCodes();
          }}
          style={{ borderColor: '#F47E46', color: '#F47E46' }}
        >
          {loading ? 'Loading…' : 'Refresh'}
        </Button>
      </Box>

      {!loading && view.cities.length === 0 && (
        <Typography variant="body2" color="textSecondary">
          No operations hierarchy data available.
        </Typography>
      )}

      {!loading && view.cities.length > 0 && (
        <Box style={{ width: '100%', overflowX: 'auto' }}>
          <Box
            display="flex"
            flexWrap="nowrap"
            alignItems="flex-start"
            style={{ gap: 16, minWidth: 'min-content' }}
          >
            {view.cities.map((cityBlock, cityIdx) => (
              <Box
                key={`city-col-${cityIdx}-${cityBlock.city}`}
                display="flex"
                flexDirection="column"
                style={{
                  flex: '0 0 auto',
                  minWidth: 300,
                  width: 420,
                  boxSizing: 'border-box',
                }}
              >
                <Box
                  py={1.2}
                  px={2}
                  style={{
                    backgroundColor: CITY_HEADER_BG,
                    color: CITY_HEADER_FG,
                    fontWeight: 700,
                    textAlign: 'center',
                    border: CELL_BORDER,
                    borderColor: CELL_BORDER_COLOR,
                  }}
                >
                  {cityBlock.city}
                </Box>

                {(cityBlock.locations || []).map((loc) => (
                  <LocationBlock
                    key={`${cityBlock.city}-${loc.ec}`}
                    loc={loc}
                    city={cityBlock.city}
                    getStatusCellBackground={getStatusCellBackground}
                  />
                ))}
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default connect(null, { setSnackInfo })(HrRacksheetOpsHierarchy);
